import { createContext, useContext, useEffect, useMemo } from 'react';

import { useBankingDataContext } from '#client/BankingDataContext';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';

export const SubScriptionContext = createContext();
SubScriptionContext.displayName = 'SubScriptionContext';

const useSubScriptionContext = () => {
  const context = useContext(SubScriptionContext);

  if (typeof context === 'undefined') {
    throw new Error('useSubScriptionContext must be used within a SubScriptionContext.Provider');
  }

  return context;
};

export function SubScriptionProvider({ children }) {
  const { createCommonErrorToast } = useToastContext();

  const { loading, data, error, errors } = useBankingDataContext();

  useLogError(errors, error);
  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const bankingSubscription = data?.bankingSubscription;
  const missingPaymentDate = bankingSubscription?.firstMissedPaymentDate ?? '';
  const subscriptionStatus = bankingSubscription?.subscriptionStatus;
  const termEndDate = bankingSubscription?.termEndDate ?? '';
  const isMissedPayment = bankingSubscription?.isMissedPayment;

  const value = useMemo(
    () => ({
      loading,
      error,
      missingPaymentDate,
      subscriptionStatus,
      termEndDate,
      isMissedPayment
    }),
    [error, isMissedPayment, loading, missingPaymentDate, subscriptionStatus, termEndDate]
  );

  return <SubScriptionContext.Provider value={value}>{children}</SubScriptionContext.Provider>;
}

export default useSubScriptionContext;
